import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Loader2, Users, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { useSWRConfig } from 'swr';
import { useCommunity } from '@/hooks/use-community';
import { useUser } from '@/hooks/use-user';
import { AxiosError } from 'axios';

export default function InviteAcceptPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const { community: communityData, isLoading: isCommunityLoading } = useCommunity(communityId);
  const { mutate: mutateUser } = useUser();
  const [joining, setJoining] = useState(false);
  const [declining, setDeclining] = useState(false);

  const community = communityData?.community;

  async function handleJoin() {
    if (!communityId) return;
    setJoining(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/join`);
      toast.success('Joined community successfully!');
      await Promise.all([mutate('/api/communities'), mutateUser()]);
      navigate(`/${communityId}/dashboard`);
    } catch (error) {
      const err = error as AxiosError<{ error: string }>;
      if (err.response?.status === 409) {
        toast.info('You are already a member');
        await Promise.all([mutate('/api/communities'), mutateUser()]);
        navigate(`/${communityId}/dashboard`);
      } else {
        toast.error(err.response?.data?.error || 'Failed to join');
      }
    } finally {
      setJoining(false);
    }
  }

  async function handleDecline() {
    if (!communityId) return;
    setDeclining(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/invites/respond`, {
        action: 'DECLINE',
      });
      toast.success('Invite declined');
      await Promise.all([mutate('/api/communities'), mutateUser()]);
      navigate('/');
    } catch {
      toast.error('Failed to decline');
    } finally {
      setDeclining(false);
    }
  }

  if (isCommunityLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!community) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Community Not Found</CardTitle>
            <CardDescription>The community you were invited to could not be found.</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button onClick={() => navigate('/')}>Go Home</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background relative overflow-hidden">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] -z-10" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] -z-10" />

      <Card className="w-full max-w-md border-border/50 bg-background/60 backdrop-blur-xl shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-20 h-20 rounded-2xl bg-secondary flex items-center justify-center overflow-hidden shadow-lg border border-border">
            {community.logo ? (
              <img
                src={community.logo}
                alt={community.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-3xl font-bold text-primary">{community.name.charAt(0)}</span>
            )}
          </div>
          <CardTitle className="text-2xl font-bold">{community.name}</CardTitle>
          <CardDescription className="flex items-center justify-center gap-2 mt-2">
            <Users className="w-4 h-4" />
            <span>You have been invited to join</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center pt-4">
          <p className="text-muted-foreground text-sm">
            Accept the invite to access the dashboard and connect with other members.
          </p>
        </CardContent>
        <CardFooter className="flex flex-col gap-3 pb-8 px-8">
          <Button
            className="w-full h-12 text-lg font-semibold bg-primary hover:bg-primary/90 shadow-lg"
            onClick={handleJoin}
            disabled={joining || declining}
          >
            {joining ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Joining...
              </>
            ) : (
              <>
                Accept Invite <ArrowRight className="ml-2 w-5 h-5" />
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={handleDecline}
            disabled={joining || declining}
            className="w-full"
          >
            {declining ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Declining...
              </>
            ) : (
              'Decline'
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            disabled={joining || declining}
            className="w-full gap-2 text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Go Back
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
